import React, { memo } from 'react';
import { useClientSnapshot } from 'site-modules/shared/hooks/use-client-snapshot';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import { get, inRange } from 'lodash-es';
import { InventoryEntities } from 'client/data/models/inventory';
import {
  UsurpAdsConfig,
  MINIMUM_CARDS,
} from 'site-modules/shared/components/inventory/usurp-card-list/usurp-card-list-ad-units-data';
import { Storage } from 'site-modules/shared/utils/storage';
import { RECENTLY_VIEWED_STORAGE } from 'site-modules/shared/components/inventory/constants/recently-viewed-vins';
import { PersonalizeSearchAdTargetingPrefetch } from 'site-modules/shared/components/native-ad/inventory-ad-targeting/personalize-search-ad-targeting';
import { useScrollOnDrawerHide } from 'site-modules/personalized-search/hooks/use-scroll-on-drawer-hide';
import { getLeadFormSrpLinkAttributes } from 'site-modules/personalized-search/utils/get-lead-form-srp-link-attributes';

function isLazyLoadImg({ index }) {
  return !inRange(index, 0, 1);
}

const AD_PROPS = {
  wrapperTagClassName: 'mb-0_75 mb-md-1_5 col-12 col-md-6 col-lg-4',
};
const EAS_AD_PROPS = {
  easListingAdClassname: 'col-lg-4',
};

const EMPTY_VINS = [];
const getRecentlyViewedVinsRaw = () => new Storage('localStorage').get(RECENTLY_VIEWED_STORAGE.PATH) || EMPTY_VINS;

export const InventoryCardList = memo(
  ({
    inventories = [],
    cardComponent: CardComponent = null,
    isMobile = false,
    cardKeyPrefix,
    onUpdateFilters,
    selectedFilters = null,
    location,
    ...rest
  }) => {
    const recentlyViewedVins = useClientSnapshot(getRecentlyViewedVinsRaw, EMPTY_VINS);

    const { onDrawerHide } = useScrollOnDrawerHide();

    const renderCard = (vehicle, index) => (
      <Col
        tag="li"
        xs={12}
        md={6}
        lg={4}
        key={`${cardKeyPrefix ? `${cardKeyPrefix}_` : ''}${vehicle.vin}`}
        className="d-flex mb-0_75 mb-md-1_5"
      >
        <CardComponent
          lazyLoadImage={isLazyLoadImg({ index })}
          vehicle={vehicle}
          importance={index === 0 ? 'high' : null}
          isViewed={recentlyViewedVins.includes(get(vehicle, 'vin'))}
          isMobile={isMobile}
          getLeadFormSrpLinkAttributes={getLeadFormSrpLinkAttributes({
            onUpdateFilters,
            selectedFilters,
            location,
          })}
          onDrawerHide={onDrawerHide}
          selectedFilters={selectedFilters}
          {...rest}
        />
      </Col>
    );

    const renderCardListWithAds = () => {
      const cardListWithAds = [];

      inventories.forEach((vehicle, index) => {
        cardListWithAds.push(renderCard(vehicle, index));
        if (UsurpAdsConfig[index + 1]) {
          cardListWithAds.push(UsurpAdsConfig[index + 1].component(PersonalizeSearchAdTargetingPrefetch, AD_PROPS));
        }
      });

      if (inventories.length < MINIMUM_CARDS) {
        cardListWithAds.push(UsurpAdsConfig.default.component(PersonalizeSearchAdTargetingPrefetch, AD_PROPS));
      } else if (UsurpAdsConfig.last) {
        cardListWithAds.push(UsurpAdsConfig.last.component(PersonalizeSearchAdTargetingPrefetch, EAS_AD_PROPS));
      }

      return cardListWithAds;
    };

    return (
      <Row
        tag="ul"
        data-tracking-parent="edm-entry-vehicle-card-list"
        className="usurp-card-list list-unstyled align-items-stretch"
      >
        {renderCardListWithAds()}
      </Row>
    );
  }
);

InventoryCardList.displayName = 'InventoryCardList';

InventoryCardList.propTypes = {
  inventories: InventoryEntities.InventoriesVin,
  cardComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isMobile: PropTypes.bool,
  cardKeyPrefix: PropTypes.string,
  onUpdateFilters: PropTypes.func.isRequired,
  selectedFilters: PropTypes.shape({}),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
};
